/**
 * Edit Profile Sheet — full profile editing form.
 * Sections: avatar, name, bio, profession, interests, contacts, personal info, privacy.
 * Interests use home categories. Gender is read-only. Visibility uses eye icon toggles.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Eye, EyeOff, Lock } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@turnifly/shared/ui'
import { AvatarWithBadge } from '@turnifly/shared/ui'
import { LoadingSpinner } from '@turnifly/shared/ui'
import { useUpdateProfile } from '@turnifly/shared/hooks'
import { useCategories } from '@turnifly/shared/hooks'
import { useMediaUpload } from '@turnifly/shared/hooks'
import { DeleteAccountModal } from './delete-account-modal'
import { triggerHapticNotification as hapticNotification, triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import type { Profile, Category } from '@turnifly/shared/types'

interface EditProfileFormData {
  full_name: string
  bio: string
  profession: string
  link_title: string
  link_url: string
  phone: string
  email: string
  birth_date: string
  country: string
  is_private: boolean
  show_gender: boolean
  show_age: boolean
  show_region: boolean
}

const NAME_REGEX = /^[a-zA-Z\u0410-\u044f\u00C0-\u017E\u02BB\u02BC\u2018\u2019\s'-]{3,40}$/

interface EditProfileSheetProps {
  isOpen: boolean
  onClose: () => void
  profile: Profile
}

export const EditProfileSheet: React.FC<EditProfileSheetProps> = ({
  isOpen, onClose, profile,
}) => {
  const { t, i18n } = useTranslation()
  const updateProfile = useUpdateProfile()
  const { data: categories, isLoading: categoriesLoading } = useCategories()
  const [isDeleteOpen, setDeleteOpen] = useState(false)
  const [selectedInterests, setSelectedInterests] = useState<string[]>([])

  const { upload: uploadAvatar, isUploading: isAvatarUploading } = useMediaUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarChanged, setAvatarChanged] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<EditProfileFormData>()

  // Reset form when sheet opens
  useEffect(() => {
    if (isOpen) {
      reset({
        full_name: profile.full_name,
        bio: profile.bio ?? '',
        profession: profile.profession ?? '',
        link_title: profile.link_title ?? '',
        link_url: profile.link_url ?? '',
        phone: profile.phone ?? '',
        email: '',
        birth_date: profile.birth_date ?? '',
        country: profile.country ?? 'Uzbekistan',
        is_private: profile.is_private,
        show_gender: profile.show_gender,
        show_age: profile.show_age,
        show_region: profile.show_region,
      })
      setSelectedInterests(profile.interests ?? [])
      setAvatarPreview(null)
      setAvatarChanged(false)
    }
  }, [isOpen, profile, reset])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto'
    return (): void => { document.body.style.overflow = 'auto' }
  }, [isOpen])

  const handleAvatarChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    if (!file) return

    // Preview
    const previewUrl = URL.createObjectURL(file)
    setAvatarPreview(previewUrl)
    hapticImpact('light')

    try {
      const result = await uploadAvatar(file, 'avatar')
      // Save avatar_url to profile
      updateProfile.mutate(
        { userId: profile.id, updates: { avatar_url: result.url } },
        {
          onSuccess: () => { hapticNotification('success'); setAvatarChanged(true) },
          onError: () => { setAvatarPreview(null); hapticNotification('error') },
        },
      )
    } catch {
      setAvatarPreview(null)
      hapticNotification('error')
    }
  }, [uploadAvatar, updateProfile, profile.id])

  const toggleInterest = useCallback((catId: string): void => {
    hapticImpact('light')
    setSelectedInterests((prev) =>
      prev.includes(catId)
        ? prev.filter((id) => id !== catId)
        : [...prev, catId],
    )
  }, [])

  const hasInterestsChanged = (): boolean => {
    const original = profile.interests ?? []
    if (original.length !== selectedInterests.length) return true
    return !original.every((id) => selectedInterests.includes(id))
  }

  const onSubmit = (data: EditProfileFormData): void => {
    const updates: Record<string, unknown> = {
      full_name: data.full_name.trim(),
      bio: data.bio.trim() || null,
      profession: data.profession.trim() || null,
      link_title: data.link_title.trim() || null,
      link_url: data.link_url.trim() || null,
      birth_date: data.birth_date || null,
      country: data.country.trim() || 'Uzbekistan',
      is_private: data.is_private,
      show_gender: data.show_gender,
      show_age: data.show_age,
      show_region: data.show_region,
      interests: selectedInterests,
    }

    updateProfile.mutate(
      { userId: profile.id, updates },
      {
        onSuccess: () => { hapticNotification('success'); onClose() },
        onError: () => { hapticNotification('error') },
      },
    )
  }

  const bioLength = watch('bio')?.length ?? 0
  const canSave = isDirty || hasInterestsChanged() || avatarChanged
  const isBusy = updateProfile.isPending || isAvatarUploading
  const genderLabel = profile.gender === 'male' ? t('profile.about.male') : t('profile.about.female')
  const catList = ((categories ?? []) as Category[])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ y: '100%', opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col
              inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh]
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md
              lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            {/* Header — close left, title center, save right */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
              <button onClick={onClose} type="button"
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('profile.editProfile')}</h2>
              <button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={!canSave || isBusy}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all tap-highlight-transparent ${
                  canSave && !isBusy
                    ? 'bg-primary text-white active:scale-95'
                    : 'bg-white/[0.06] text-text-hint/40'
                }`}
              >
                {isBusy ? <LoadingSpinner size="sm" /> : t('common.save')}
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-5 pb-8">
              {/* Avatar with upload */}
              <div className="flex flex-col items-center mb-6 gap-2.5">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAvatarUploading}
                  className="relative tap-highlight-transparent"
                >
                  <AvatarWithBadge
                    src={avatarPreview ?? profile.avatar_url}
                    fallback={profile.full_name.charAt(0)}
                    size="xl"
                  />

                  {/* Loading overlay */}
                  {isAvatarUploading && (
                    <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void handleAvatarChange(e)}
                />

                {/* Text link only — no icon */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAvatarUploading}
                  className="text-[13px] text-primary font-semibold tap-highlight-transparent disabled:text-text-hint"
                >
                  {isAvatarUploading ? t('common.uploading') : t('profile.edit.changePhoto')}
                </button>
              </div>

              <div className="space-y-5">
                {/* ── Asosiy ma'lumotlar ── */}
                <SectionLabel label={t('profile.edit.basicInfo')} />

                <Input label={t('profile.edit.fullName')}
                  {...register('full_name', {
                    required: t('profile.edit.nameRequired'),
                    pattern: { value: NAME_REGEX, message: t('profile.edit.nameInvalid') },
                  })}
                  error={errors.full_name?.message} />

                <div>
                  <label className="block text-sm font-medium text-text mb-1.5">{t('profile.edit.bio')}</label>
                  <textarea {...register('bio', { maxLength: 300 })} rows={3} maxLength={300}
                    placeholder={t('profile.edit.bioPlaceholder')}
                    className="w-full px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.06]
                      text-text text-sm placeholder:text-text-hint/50 resize-none
                      focus:outline-none focus:border-primary/30 transition-colors" />
                  <span className={`text-xs mt-1 block text-right ${bioLength >= 280 ? 'text-warning' : 'text-text-hint'}`}>
                    {bioLength}/300
                  </span>
                </div>

                <Input label={t('profile.edit.profession')} placeholder={t('profile.edit.professionPlaceholder')}
                  {...register('profession')} />

                {/* ── Qiziqishlar ── */}
                <SectionLabel label={t('profile.edit.interests')} />

                {categoriesLoading ? (
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="h-8 w-20 rounded-full bg-white/[0.04] animate-pulse" />
                    ))}
                  </div>
                ) : catList.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {catList.map((cat) => {
                      const isSelected = selectedInterests.includes(cat.id)
                      const label = i18n.language === 'uz' ? cat.name_uz : cat.name_en
                      return (
                        <button key={cat.id} type="button" onClick={() => toggleInterest(cat.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                            transition-all border tap-highlight-transparent
                            ${isSelected
                              ? 'bg-primary/15 border-primary/30 text-primary'
                              : 'bg-white/[0.03] border-white/[0.06] text-text-hint hover:bg-white/[0.06]'}`}>
                          <span>{cat.icon}</span>
                          <span>{label}</span>
                        </button>
                      )
                    })}
                  </div>
                ) : null}

                {/* ── Kontaktlar ── */}
                <SectionLabel label={t('profile.edit.contacts')} />

                <div>
                  <label className="block text-sm font-medium text-text mb-1.5">{t('profile.edit.phone')}</label>
                  <div className="flex items-center px-4 py-3 rounded-2xl bg-white/[0.02] border border-white/[0.04] text-sm text-text-hint">
                    <Lock size={14} className="mr-2 text-text-hint/40" />
                    <span>{profile.phone ?? t('profile.edit.noPhone')}</span>
                  </div>
                </div>

                <Input label={t('profile.edit.linkTitle')} placeholder="GitHub, Portfolio..."
                  {...register('link_title')} />

                <Input label={t('profile.edit.linkUrl')} placeholder="https://"
                  {...register('link_url', {
                    validate: (value: string | null | undefined) => {
                      if (!value) return true
                      try { new URL(value); return true } catch { return t('profile.edit.invalidUrl') }
                    },
                  })}
                  error={errors.link_url?.message} />

                {/* ── Shaxsiy ma'lumotlar ── */}
                <SectionLabel label={t('profile.edit.personalInfo')} />

                {/* Gender — read-only */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-text">{t('profile.edit.gender')}</span>
                    <span className="text-sm text-text-hint ml-2">{genderLabel}</span>
                  </div>
                  <VisibilityToggle
                    isVisible={watch('show_gender')}
                    onToggle={() => setValue('show_gender', !watch('show_gender'), { shouldDirty: true })}
                  />
                </div>

                {/* Birth date */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <Input label={t('profile.edit.birthDate')} type="date"
                      {...register('birth_date')} />
                  </div>
                  <div className="pt-6">
                    <VisibilityToggle
                      isVisible={watch('show_age')}
                      onToggle={() => setValue('show_age', !watch('show_age'), { shouldDirty: true })}
                    />
                  </div>
                </div>

                {/* Country */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <Input label={t('profile.edit.country')} placeholder="Uzbekistan"
                      {...register('country')} />
                  </div>
                  <div className="pt-6">
                    <VisibilityToggle
                      isVisible={watch('show_region')}
                      onToggle={() => setValue('show_region', !watch('show_region'), { shouldDirty: true })}
                    />
                  </div>
                </div>

                {/* ── Maxfiylik ── */}
                <SectionLabel label={t('profile.edit.privacy')} />

                <PrivacyToggle
                  label={t('profile.edit.privateProfile')}
                  description={t('profile.edit.privateDesc')}
                  {...register('is_private')}
                />
              </div>

              {/* Delete account — at bottom */}
              <div className="mt-8 pt-4 border-t border-white/[0.04]">
                <button type="button" onClick={() => setDeleteOpen(true)}
                  className="w-full py-3 text-sm text-danger/70 hover:text-danger transition-colors tap-highlight-transparent">
                  {t('profile.edit.deleteAccount')}
                </button>
              </div>
            </form>
          </motion.div>

          {isDeleteOpen && <DeleteAccountModal isOpen onClose={() => setDeleteOpen(false)} />}
        </>
      )}
    </AnimatePresence>
  )
}

// ── Section Label ───────────────────────────────────────────

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <div className="pt-2 pb-1">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-text-hint/60">
      {label}
    </span>
  </div>
)

// ── Visibility Toggle (eye icon) ────────────────────────────

interface VisibilityToggleProps {
  isVisible: boolean
  onToggle: () => void
}

const VisibilityToggle: React.FC<VisibilityToggleProps> = ({ isVisible, onToggle }) => (
  <button type="button" onClick={onToggle}
    className="w-9 h-9 flex items-center justify-center rounded-full
      hover:bg-white/[0.04] transition-colors tap-highlight-transparent">
    {isVisible
      ? <Eye size={16} className="text-primary" />
      : <EyeOff size={16} className="text-text-hint/40" />
    }
  </button>
)

// ── Privacy Toggle (switch for is_private) ──────────────────

interface PrivacyToggleProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  description?: string
}

const PrivacyToggle = React.forwardRef<HTMLInputElement, PrivacyToggleProps>(
  ({ label, description, ...props }, ref) => (
    <label className="flex items-center justify-between py-2.5 cursor-pointer tap-highlight-transparent">
      <div className="flex-1 min-w-0 mr-3">
        <span className="text-sm text-text">{label}</span>
        {description && <p className="text-xs text-text-hint mt-0.5">{description}</p>}
      </div>
      <div className="relative shrink-0">
        <input type="checkbox" ref={ref} {...props} className="peer sr-only" />
        <div className="w-11 h-6 rounded-full bg-white/[0.08] peer-checked:bg-primary transition-colors" />
        <div className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-text shadow-sm transition-transform peer-checked:translate-x-5" />
      </div>
    </label>
  ),
)
PrivacyToggle.displayName = 'PrivacyToggle'
